import { useState } from 'react';
import { FeatureFlags, TreeNode, TreeView } from '@carbon/react';
import { StatusNodeLabel } from './status-node-label';
import type { StructuringTreeNode } from './types';

interface StructuringTreeProps {
  activeNodeId: string;
  // Null when the document has no structured overview.
  overviewNodes: StructuringTreeNode[] | null;
  onSelectProvisions: () => void;
  onSelectOverview: () => void;
}

/**
 * The structuring left-nav: Provisions, then the Structured Overview's
 * collapsible section tree.
 *
 * @param {StructuringTreeProps} props - The component props.
 * @return {JSX.Element} The tree view.
 */
export function StructuringTree({
  activeNodeId,
  overviewNodes,
  onSelectProvisions,
  onSelectOverview,
}: StructuringTreeProps) {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  /**
   * Expands or collapses a node.
   *
   * @param {string} id - The node id.
   * @return {void}
   */
  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  /**
   * Renders a node and, recursively, its children.
   *
   * @param {StructuringTreeNode} node - The node to render.
   * @return {JSX.Element} The tree node.
   */
  const renderNode = (node: StructuringTreeNode) => (
    <TreeNode
      key={node.id}
      id={node.id}
      label={
        node.status ? (
          <StatusNodeLabel
            text={node.text}
            status={node.status}
            removed={node.removed}
            running={node.running}
          />
        ) : (
          node.text
        )
      }
      onSelect={node.onSelect}
      {...(node.children && {
        isExpanded: !collapsed.has(node.id),
        onToggle: () => toggle(node.id),
      })}
    >
      {node.children?.map(renderNode)}
    </TreeNode>
  );

  return (
    // Scroll-spy moves the active node, so `active`/`selected` must stay
    // authoritative rather than being read once.
    <FeatureFlags enableTreeviewControllable>
      <TreeView
        label="Structuring"
        hideLabel
        active={activeNodeId}
        selected={[activeNodeId]}
      >
        <TreeNode
          id="provisions"
          label="Provisions"
          onSelect={onSelectProvisions}
        />
        {overviewNodes &&
          renderNode({
            id: 'structured-overview',
            text: 'Structured Overview',
            onSelect: onSelectOverview,
            children: overviewNodes,
          })}
      </TreeView>
    </FeatureFlags>
  );
}
